// What the Publish page says about a format before it's built (PublishRoom.swift's `settingsRows`, LibraryModel+Publish's
// `AudiobookEstimate`): the resolved preset in the writer's terms, and an audiobook's tracks with estimated lengths.
// Pure functions of the compiled manuscript, run in the compile worker.
import { BookMatter, isbnOf } from '@writing-suite/core';
import {
  describeFormat, isDocument, isPost, keepsMarks, publicationEdition, resolvedMargins, type CompiledManuscript, type LastPrint, type NarrationScript,
  type PackDocument,
} from '@writing-suite/compile';
import type { AudiobookEstimate } from './protocol.ts';

const number = (x: number, digits = 2) => x.toLocaleString('en-GB', { maximumFractionDigits: digits });
const words = (n: number) => `${n.toLocaleString('en-GB')} ${n === 1 ? 'word' : 'words'}`;

/** A print preset's page count: the last build's when it was this preset at this trim, else about words ÷ 290. */
export function printPages(m: CompiledManuscript, last: LastPrint | null): { pages: number; built: boolean } {
  if (last && last.presetID === m.preset.id && last.size === m.preset.render.page.size) return { pages: last.pages, built: true };
  return { pages: Math.max(1, Math.floor(m.wordCount / 290)), built: false };
}

/** A submission pack's documents: each one's words, or that it isn't written. */
export function packRows(documents: PackDocument[]): [string, string][] {
  return documents.map((d) => [
    BookMatter.title(d.kind),
    d.words === 0 ? (d.kind === 'cast-list' ? 'Made from who speaks' : 'Not written')
      : words(d.words) + (d.kind === 'synopsis' ? ` · about ${Math.max(1, Math.round(d.words / 500))} ${Math.max(1, Math.round(d.words / 500)) === 1 ? 'page' : 'pages'}` : ''),
  ]);
}

/** `settingsRows`: what the format will be built with, read from its resolved preset. */
export function settingsRows(m: CompiledManuscript, o: { lastPrint: LastPrint | null; postNumber?: number | null }): [string, string][] {
  const preset = m.preset, r = preset.render;
  if (preset.format === 'audiobook' || r.audio) {
    const audio = m.audiobook;
    const voice = audio.engine === 'elevenlabs' ? `ElevenLabs · ${audio.elevenLabsVoiceName ?? 'not chosen'}` : audio.systemVoice ? 'Chosen on your Mac or iPad' : 'Best voice on the device';
    const pronunciations = Object.keys(audio.pronunciations).length;
    return [['Voice', voice], ['Narrator', audio.narrator.trim() === '' ? 'Not credited' : audio.narrator],
      ['Scene breaks', `${number(r.audio?.sceneBreak ?? 2)} s pause`], ['Pronunciations', pronunciations ? pronunciations.toLocaleString('en-GB') : 'None yet'],
      ['File', describeFormat(preset.format)]];
  }
  if (r.screenplay?.format === 'radio') {
    return [['Page', r.page.size === 'a4' ? 'A4' : 'US Letter'], ['Speech numbers', r.screenplay.speechNumbers === 'page' ? 'From 1 on each page' : 'None'],
      ['Title page', r.titlePage.include ? 'Included' : 'None'], ['Cast list', preset.select.frontMatter ? 'Included' : 'None'], ['File', describeFormat(preset.format)]];
  }
  if (r.screenplay?.format === 'multicam') {
    return [['Page', r.page.size === 'a4' ? 'A4' : 'US Letter'], ['Scene letter in header', r.screenplay.sceneLetterInHeader ? 'Yes' : 'No'],
      ['Title page', r.titlePage.include ? 'Included' : 'None'], ['File', describeFormat(preset.format)]];
  }
  if (r.screenplay) {
    return [['Page', r.page.size === 'a4' ? 'A4' : 'US Letter'], ['Scene numbers', r.screenplay.sceneNumbers ? 'Both margins' : 'None'],
      ['Title page', r.titlePage.include ? 'Included' : 'None'], ['File', describeFormat(preset.format)]];
  }
  const rows: [string, string][] = [];
  if (isPost(m)) {
    const post = m.sections.find((s) => s.fragmentID !== null && s.matter === null);
    const provisional = post?.fragmentID ? m.provisionalNumbers[post.fragmentID] : undefined;
    rows.push(['Number', o.postNumber != null ? `No. ${o.postNumber}` : provisional !== undefined ? `No. ${provisional}, until it goes out` : 'None']);
    rows.push(['Words', m.wordCount.toLocaleString('en-GB')]);
    const own = !!post?.fragmentID && (m.ownPostMatter ?? []).includes(post.fragmentID);
    rows.push(['Around it', own ? 'Its own opening and sign-off' : preset.select.postMatter ? 'Opening and sign-off' : 'Nothing']);
    rows.push(['File', describeFormat(preset.format)]);
    return rows;
  }
  const kind = BookMatter.from(preset.select.document);
  if (kind) rows.push(['Document', `${BookMatter.title(kind)}, from Publishing`]);
  if (r.print) {
    const margins = resolvedMargins(r.page);
    const counted = printPages(m, o.lastPrint);
    rows.push(['Trim', `${r.page.size.replace('x', ' × ')} in`], ['Body', `${r.font.family} ${number(r.font.size)} / ${number(r.font.size * r.lineSpacing, 1)}`],
      ['Margins', `${number(margins.inside)} in inside, ${number(margins.outside)} outside`],
      ['Pages', counted.built ? counted.pages.toLocaleString('en-GB') : `about ${counted.pages.toLocaleString('en-GB')}`]);
    return rows;
  }
  if (preset.format === 'epub') {
    const isbn = isbnOf(m.publication, 'ebook');
    let language = m.language;
    try {
      language = new Intl.DisplayNames(['en-GB'], { type: 'language' }).of(m.language) ?? m.language;
    } catch {
      // an unreadable tag is shown as it is
    }
    return [['ISBN', isbn === '' ? 'None' : isbn], ['Contents', 'Linked, generated'], ['Language', language],
      ['Scene breaks', r.sceneBreak === '' ? 'Blank line' : r.sceneBreak]];
  }
  rows.push(['Font', `${r.font.family} ${number(r.font.size)}`],
    ['Spacing', r.lineSpacing === 2 ? 'Double' : r.lineSpacing === 1 ? 'Single' : `${number(r.lineSpacing)} lines`]);
  if (preset.format !== 'text' && preset.format !== 'markdown') rows.push(['Italics', r.italicAsUnderline ? 'Underlined' : 'Italic']);
  if (preset.format === 'docx') rows.push(['Highlights', keepsMarks(preset) ? 'Kept' : 'Left out']);
  if (!isDocument(m)) rows.push(['Scene breaks', r.sceneBreak === '' ? 'Blank line' : r.sceneBreak]);
  rows.push(['File', describeFormat(preset.format)]);
  return rows;
}

/** The edition's ISBN and publisher, as the copyright page gives them. */
export function publicationSummary(m: CompiledManuscript): { edition: string | null; isbn: string; publisher: string; copyright: string } {
  const edition = publicationEdition(m.preset);
  const p = m.publication;
  return {
    edition, isbn: edition ? isbnOf(p, edition) : '', publisher: p.publisher.trim(),
    copyright: [p.copyrightYear.trim(), p.copyrightHolder.trim()].filter(Boolean).join(' '),
  };
}

/** `AudiobookEstimate.from`: a voice reads about 15 characters a second at the usual pace, plus the script's pauses. */
export function audiobookEstimate(script: NarrationScript): AudiobookEstimate {
  const characters = (t: NarrationScript['tracks'][number]) => t.segments.reduce((n, s) => n + (s.type === 'speech' ? [...s.text].length : 0), 0);
  const tracks = script.tracks.map((t) => ({
    id: t.id, title: t.title,
    seconds: t.segments.reduce((total, s) => total + (s.type === 'speech' ? [...s.text].length / 15 : s.seconds), 0),
  }));
  const story = script.tracks.find((t) => t.fragmentID !== null && characters(t) > 200) ?? script.tracks[0];
  let sample = '';
  for (const s of story?.segments ?? []) {
    if (s.type !== 'speech') continue;
    if ([...sample].length >= 500) break;
    sample += (sample === '' ? '' : ' ') + s.text;
  }
  return { tracks, total: tracks.reduce((n, t) => n + t.seconds, 0), sampleTitle: story?.title ?? 'Sample', sampleText: sample };
}

/** "1 h 5 m", "12 m" (long) or "1:05:00", "12:04" (short), as the Mac's estimate card shows lengths. */
export function clock(seconds: number, long: boolean): string {
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600), m = Math.floor((total % 3600) / 60), s = total % 60;
  if (long) return h > 0 ? `${h} h ${m} m` : `${Math.max(1, m)} m`;
  const two = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${two(m)}:${two(s)}` : `${m}:${two(s)}`;
}
